import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { fetchTopics, type TopicSummary } from '@/lib/lingodb'
import { getPatientToken } from '@/lib/auth-token'
import { isNative } from '@/lib/platform'
import {
  downloadThumbnail,
  loadCachedTopics,
  pruneOrphans,
  saveCachedTopics,
  type CachedTopic,
} from '@/lib/thumbnail-cache'

/**
 * In-memory store for the content-library topic list, backed by a
 * Filesystem-persisted cache so the topic-picker screen opens instantly
 * even on a cold launch with no network.
 *
 * Lifecycle:
 *   1. `preload()` runs once at app mount (from App.vue) and once again
 *      right after first-launch bootstrap (from LaunchTerms.onSubmit).
 *      It is idempotent — concurrent calls share one inflight promise.
 *   2. On invocation it hydrates `topics` from the on-disk JSON index for
 *      instant UI, then refreshes from the proxy and downloads any new or
 *      changed thumbnail bytes.
 *   3. The "fresh" guard (10 min) skips the network round-trip if a
 *      successful refresh just happened — relevant when LaunchTrialPatient
 *      remounts or when the user backgrounds + foregrounds the app.
 *
 * The store deliberately does not enforce auth state: if no patient token
 * exists yet, network refresh is silently skipped. The on-mount caller
 * fires again after bootstrap and picks up where we left off.
 */
export const useTopicsStore = defineStore('topics', () => {
  const topics = ref<CachedTopic[]>([])
  const status = ref<'idle' | 'hydrating' | 'loading' | 'ready' | 'error'>('idle')
  const lastFetchedAt = ref<Date | null>(null)
  const error = ref<string | null>(null)

  /** Shared promise so concurrent preload() callers fan in. */
  let inflight: Promise<void> | null = null

  const FRESH_WINDOW_MS = 10 * 60 * 1000

  const isReady = computed(() => status.value === 'ready')

  function getById(id: number): CachedTopic | undefined {
    return topics.value.find((t) => t.id === id)
  }

  function isFresh(): boolean {
    if (lastFetchedAt.value === null) return false
    return Date.now() - lastFetchedAt.value.getTime() < FRESH_WINDOW_MS
  }

  /**
   * Walk the proxy's cursor pagination to completion. The topic count is
   * small (low hundreds at most for the foreseeable future) so paginating
   * to completion at preload time is the simplest correct thing.
   */
  async function fetchAllPages(): Promise<TopicSummary[]> {
    const all: TopicSummary[] = []
    let cursor: string | undefined
    do {
      const page = await fetchTopics({ cursor, perPage: 50 })
      all.push(...page.data)
      cursor = page.meta.next_cursor ?? undefined
    } while (cursor)
    return all
  }

  /**
   * Diff incoming topics against the on-disk cache to decide which
   * thumbnails to (re)download. We re-fetch when:
   *   - the topic is new to us, OR
   *   - the upstream `updated_at` is newer than what we cached, OR
   *   - we previously failed to cache a thumbnail (local URI is null).
   */
  async function syncThumbnails(incoming: TopicSummary[]): Promise<CachedTopic[]> {
    const previousById = new Map(topics.value.map((t) => [t.id, t]))

    const next: CachedTopic[] = []
    for (const t of incoming) {
      const prev = previousById.get(t.id)
      const upstreamHasCover = t.cover_image !== null
      const needsDownload =
        upstreamHasCover &&
        (
          prev === undefined ||
          prev.updated_at !== t.updated_at ||
          prev.thumbnail_local_uri === null
        )

      let local_uri = prev?.thumbnail_local_uri ?? null
      let cached_at = prev?.thumbnail_cached_at ?? null

      if (needsDownload && t.cover_image) {
        try {
          local_uri = await downloadThumbnail(t.id, t.cover_image.url)
          cached_at = new Date().toISOString()
        } catch (e) {
          // Keep the prior URI (if any) so the UI still has something to
          // render; the next preload will retry.
          console.warn(`thumbnail download failed for topic ${t.id}:`, e)
        }
      } else if (!upstreamHasCover) {
        local_uri = null
        cached_at = null
      }

      next.push({
        ...t,
        thumbnail_local_uri: local_uri,
        thumbnail_cached_at: cached_at,
      })
    }

    return next
  }

  async function refresh(): Promise<void> {
    status.value = topics.value.length > 0 ? 'hydrating' : 'loading'
    error.value = null
    try {
      const all = await fetchAllPages()
      const enriched = await syncThumbnails(all)

      topics.value = enriched
      lastFetchedAt.value = new Date()
      status.value = 'ready'

      // Persist after state update so the cache always reflects what's in
      // memory if the user immediately navigates somewhere.
      await saveCachedTopics(enriched)
      await pruneOrphans(enriched.map((t) => t.id))
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      // Keep showing cached topics if we have them; only "error" if empty.
      status.value = topics.value.length > 0 ? 'ready' : 'error'
    }
  }

  async function preload(): Promise<void> {
    if (inflight) return inflight
    if (isFresh()) return

    inflight = (async () => {
      try {
        // 1. Hydrate from disk so the UI has something to show now.
        if (topics.value.length === 0) {
          status.value = 'hydrating'
          const cached = await loadCachedTopics()
          if (cached.length > 0) {
            topics.value = cached
          }
        }

        // 2. Refresh from the network only if we already have auth. On the
        //    very first launch no token exists yet — LaunchTerms re-fires
        //    preload() after auth.bootstrapPatient() completes.
        if (isNative) {
          const token = await getPatientToken()
          if (!token) {
            status.value = topics.value.length > 0 ? 'ready' : 'idle'
            return
          }
        }

        await refresh()
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  return {
    // state
    topics,
    status,
    lastFetchedAt,
    error,
    // getters
    isReady,
    // actions
    preload,
    refresh,
    getById,
  }
})
